// src/components/notifications/NotificationDropdown.js
// Notification dropdown panel — renders over any page from TopNav bell icon

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, writeBatch, where, Timestamp, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useToast } from '../shared/contexts/ToastContext';
import { Link } from 'react-router-dom';
import {
    FiBell, FiCheck, FiCheckCircle, FiAlertCircle, FiImage, FiRefreshCw,
    FiLoader, FiInbox, FiChevronRight, FiLink, FiUnlock
} from 'react-icons/fi';

// --- Notification Type Icons & Colors ---
const NOTIFICATION_STYLES = {
    post_published: { icon: <FiCheckCircle className="h-4 w-4" />, iconColor: 'text-pastel-green', bgColor: 'bg-pastel-green-bg/30', borderColor: 'border-green-400/20' },
    post_failed: { icon: <FiAlertCircle className="h-4 w-4" />, iconColor: 'text-soft-coral', bgColor: 'bg-soft-coral/10', borderColor: 'border-soft-coral/20' },
    stream_complete: { icon: <FiRefreshCw className="h-4 w-4" />, iconColor: 'text-slate-blue', bgColor: 'bg-slate-blue/10', borderColor: 'border-slate-blue/20' },
    stream_error: { icon: <FiAlertCircle className="h-4 w-4" />, iconColor: 'text-soft-coral', bgColor: 'bg-soft-coral/10', borderColor: 'border-soft-coral/20' },
    visual_ready: { icon: <FiImage className="h-4 w-4" />, iconColor: 'text-slate-blue', bgColor: 'bg-slate-blue/10', borderColor: 'border-slate-blue/20' },
    visual_failed: { icon: <FiImage className="h-4 w-4" />, iconColor: 'text-soft-coral', bgColor: 'bg-soft-coral/10', borderColor: 'border-soft-coral/20' },
    meta_account: {
        icon: <FiLink className="h-4 w-4" />, iconColor: 'text-blue-400', bgColor: 'bg-blue-400/10', borderColor: 'border-blue-400/20',
        disconnectedIcon: <FiUnlock className="h-4 w-4" />, disconnectedIconColor: 'text-cream/60', disconnectedBgColor: 'bg-warm-gray/10', disconnectedBorderColor: 'border-white/10'
    },
    default: { icon: <FiBell className="h-4 w-4" />, iconColor: 'text-cream/60', bgColor: 'bg-warm-gray/10', borderColor: 'border-white/5' }
};

// --- Format Relative Time ---
const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// --- Compact Notification Item ---
const DropdownNotificationItem = ({ notification, onMarkAsRead, onNavigate }) => {
    const baseStyle = NOTIFICATION_STYLES[notification.type] || NOTIFICATION_STYLES.default;
    const data = notification.data || {};
    const isDisconnected = notification.type === 'meta_account' && data.action === 'disconnected';
    const style = {
        icon: isDisconnected ? (baseStyle.disconnectedIcon || baseStyle.icon) : baseStyle.icon,
        iconColor: isDisconnected ? (baseStyle.disconnectedIconColor || baseStyle.iconColor) : baseStyle.iconColor,
        bgColor: isDisconnected ? (baseStyle.disconnectedBgColor || baseStyle.bgColor) : baseStyle.bgColor,
        borderColor: isDisconnected ? (baseStyle.disconnectedBorderColor || baseStyle.borderColor) : baseStyle.borderColor,
    };

    return (
        <div className={`px-4 py-3 flex items-start gap-3 transition-colors hover:bg-white/5 ${
            notification.read ? 'opacity-50' : ''
        }`}>
            {/* Icon */}
            <div className={`p-1.5 rounded-lg flex-shrink-0 mt-0.5 ${notification.read ? 'bg-warm-gray/10 text-cream/30' : `${style.bgColor} ${style.iconColor}`}`}>
                {style.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                {/* Context badges */}
                {data.projectName && (
                    <span className="text-[9px] font-bold text-cream/50 bg-white/10 px-1.5 py-0.5 rounded inline-block mb-1">
                        {data.projectName}
                    </span>
                )}
                <p className={`text-xs leading-relaxed ${notification.read ? 'text-cream/40' : 'text-cream/90'}`}>
                    {notification.message}
                </p>
                <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-cream/25">{formatRelativeTime(notification.createdAt)}</span>
                    {data.projectId && (
                        <Link
                            to={`/project/${data.projectId}`}
                            onClick={onNavigate}
                            className="text-[10px] text-slate-blue hover:text-cream transition-colors flex items-center font-medium"
                        >
                            View <FiChevronRight className="h-2.5 w-2.5 ml-0.5" />
                        </Link>
                    )}
                </div>
            </div>

            {/* Mark as read */}
            {!notification.read && (
                <button
                    onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.id); }}
                    className="p-1 rounded-md hover:bg-white/10 text-cream/30 hover:text-slate-blue transition-colors flex-shrink-0 mt-0.5"
                    title="Mark as read"
                >
                    <FiCheck className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
};

// --- Main Dropdown Component ---
function NotificationDropdown({ userId, onClose }) {
    const { addToast } = useToast();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingAll, setMarkingAll] = useState(false);

    const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

    // --- Fetch notifications ---
    useEffect(() => {
        if (!userId) { setLoading(false); return; }

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const notificationsRef = collection(db, 'users', userId, 'notifications');
        const q = query(
            notificationsRef,
            where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
            orderBy('createdAt', 'desc'),
            limit(30)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setNotifications(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, () => { setLoading(false); });

        return () => unsubscribe();
    }, [userId]);

    // --- Mark single as read ---
    const handleMarkAsRead = useCallback(async (notificationId) => {
        if (!userId || !notificationId) return;
        try {
            await updateDoc(doc(db, 'users', userId, 'notifications', notificationId), { read: true });
        } catch {
            addToast('Failed to mark notification as read', 'error');
        }
    }, [userId, addToast]);

    // --- Mark all as read ---
    const handleMarkAllAsRead = useCallback(async () => {
        if (!userId || unreadCount === 0) return;
        setMarkingAll(true);
        try {
            const allUnreadQuery = query(
                collection(db, 'users', userId, 'notifications'),
                where('read', '==', false),
                limit(500)
            );
            const snap = await getDocs(allUnreadQuery);
            if (snap.empty) { setMarkingAll(false); return; }

            const batch = writeBatch(db);
            snap.docs.forEach(d => {
                batch.update(doc(db, 'users', userId, 'notifications', d.id), { read: true });
            });
            await batch.commit();
            addToast(`Marked ${snap.size} notifications as read`, 'success');
        } catch {
            addToast('Failed to mark all as read', 'error');
        } finally {
            setMarkingAll(false);
        }
    }, [userId, unreadCount, addToast]);

    return (
        <div onMouseDown={(e) => e.stopPropagation()} className="origin-top-right absolute right-0 mt-3 w-96 max-w-[calc(100vw-2rem)] rounded-2xl shadow-modal bg-warm-gray border border-warm-border ring-1 ring-black ring-opacity-5 z-50 animate-fade-in-up backdrop-blur-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-cream">Notifications</h3>
                    {unreadCount > 0 && (
                        <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-soft-coral text-white text-[10px] font-bold px-1.5">
                            {unreadCount}
                        </span>
                    )}
                </div>
                {unreadCount > 0 && (
                    <button
                        onClick={handleMarkAllAsRead}
                        disabled={markingAll}
                        className="text-[10px] font-bold uppercase tracking-wider text-slate-blue hover:text-cream transition-colors flex items-center gap-1"
                    >
                        {markingAll ? <FiLoader className="h-3 w-3 animate-spin" /> : <FiCheckCircle className="h-3 w-3" />}
                        Mark all read
                    </button>
                )}
            </div>

            {/* Notification list */}
            <div className="max-h-[420px] overflow-y-auto custom-scrollbar">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-2">
                        <FiLoader className="h-6 w-6 text-slate-blue animate-spin" />
                        <p className="text-[10px] text-cream/30 font-bold uppercase tracking-wider">Loading...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-2">
                        <FiInbox className="h-8 w-8 text-cream/10" />
                        <p className="text-xs text-cream/30 font-medium">No notifications yet</p>
                    </div>
                ) : (
                    <div className="divide-y divide-white/5 py-1">
                        {notifications.map(notification => (
                            <DropdownNotificationItem
                                key={notification.id}
                                notification={notification}
                                onMarkAsRead={handleMarkAsRead}
                                onNavigate={onClose}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default NotificationDropdown;
